import { useEffect, useMemo, useRef, useState } from "react";
import { createWavemakerGame, type WavemakerGameHandle } from "../index.js";
import { getTranslate, onLocaleChange, setLocale } from "../i18n";
import "./wavemaker.css";
import winSoundSrc from "../../assets/sounds/win.mp3";
import winSoundCelebrationSrc from "../../assets/sounds/win2.mp3";
import gearIconSrc from "../../assets/images/gear-icon.svg";

export interface WavemakerGameProps {
  /** Optional CSS class passed to the root wrapper. */
  className?: string;
  locale?: string;
}

function FlipHorizontalIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M7 7 2 12l5 5m10-10 5 5-5 5M2 12h20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function WavemakerGame({ className, locale = "en" }: WavemakerGameProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const gameRef = useRef<WavemakerGameHandle | null>(null);
  const initialLocaleRef = useRef(locale);
  const levelDropdownRef = useRef<HTMLDivElement | null>(null);
  const speedDropdownRef = useRef<HTMLDivElement | null>(null);
  const speedMenuRef = useRef<HTMLDivElement | null>(null);
  const levelButtonRef = useRef<HTMLButtonElement | null>(null);
  const [openMenu, setOpenMenu] = useState<"speed" | "level" | null>(null);
  const [isSoundOn, setIsSoundOn] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showReferenceLines, setShowReferenceLines] = useState(false);
  const [levelButtonLabel, setLevelButtonLabel] = useState(() =>
    getTranslate().t("ui.levelWithNumber", { level: 1 })
  );
  const [timeSpeedButtonLabel, setTimeSpeedButtonLabel] = useState(() =>
    getTranslate().t("ui.timeSpeed", { speed: "1x" })
  );
  const [, setLocaleRevision] = useState(0);

  useEffect(() => {
    setLocale(locale);
  }, [locale]);

  useEffect(() => {
    const unsubscribe = onLocaleChange(() => {
      setLocaleRevision((value) => value + 1);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const instance = createWavemakerGame({
      canvas: canvasRef.current,
      locale: initialLocaleRef.current,
      onLevelLabelChange: (label) => setLevelButtonLabel(label),
      onTimeSpeedLabelChange: (label) => setTimeSpeedButtonLabel(label),
    });
    gameRef.current = instance;

    return () => {
      gameRef.current = null;
      instance?.destroy?.();
    };
  }, []);

  useEffect(() => {
    if (isSettingsOpen) {
      gameRef.current?.pause?.();
    } else {
      gameRef.current?.resume?.();
    }
  }, [isSettingsOpen]);

  useEffect(() => {
    gameRef.current?.setShowReferenceLines?.(showReferenceLines);
  }, [showReferenceLines]);

  useEffect(() => {
    if (!openMenu) {
      return;
    }

    function handlePointerDown(event: MouseEvent | PointerEvent | TouchEvent) {
      const target = event.target;
      if (!(target instanceof Node)) {
        setOpenMenu(null);
        return;
      }

      const activeDropdown =
        openMenu === "level"
          ? levelDropdownRef.current
          : openMenu === "speed"
            ? speedDropdownRef.current
            : null;

      if (activeDropdown && !activeDropdown.contains(target)) {
        setOpenMenu(null);
      }
    }

    const hasPointerEvents = typeof window !== "undefined" && "PointerEvent" in window;
    if (hasPointerEvents) {
      document.addEventListener("pointerdown", handlePointerDown, true);
      return () => document.removeEventListener("pointerdown", handlePointerDown, true);
    }

    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("touchstart", handlePointerDown, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("touchstart", handlePointerDown, true);
    };
  }, [openMenu]);

  useEffect(() => {
    const speedMenu = speedMenuRef.current;
    const speedDropdown = speedDropdownRef.current;
    if (openMenu !== "speed" || !speedMenu || !speedDropdown) {
      speedMenu?.style.removeProperty("max-height");
      return;
    }

    const updateMaxHeight = () => {
      const rect = speedDropdown.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const verticalMargin = 8;
      const availableBelow = Math.max(0, Math.floor(viewportHeight - rect.bottom - verticalMargin));
      const defaultMaxHeight = 220;
      const resolvedMaxHeight = Math.min(defaultMaxHeight, availableBelow);
      speedMenu.style.maxHeight = `${resolvedMaxHeight}px`;
    };

    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    window.addEventListener("orientationchange", updateMaxHeight);
    return () => {
      window.removeEventListener("resize", updateMaxHeight);
      window.removeEventListener("orientationchange", updateMaxHeight);
      speedMenu.style.removeProperty("max-height");
    };
  }, [openMenu]);

  useEffect(() => {
    if (openMenu !== "level") {
      levelButtonRef.current?.blur();
    }
  }, [openMenu]);

  useEffect(() => {
    if (!openMenu) {
      return;
    }

    const closeMenu = () => setOpenMenu(null);
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        closeMenu();
      }
    };

    window.addEventListener("blur", closeMenu);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("blur", closeMenu);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [openMenu]);

  const dropdownClassNames = useMemo(
    () => ({
      speed: `dropdown ${openMenu === "speed" ? "dropdown-open" : ""}`,
      level: `dropdown ${openMenu === "level" ? "dropdown-open" : ""}`,
    }),
    [openMenu]
  );

  const translate = getTranslate();
  const t = (key: string, vars?: Record<string, unknown>) => translate.t(key, vars);

  function openSettingsModal() {
    setOpenMenu(null);
    setIsSettingsOpen(true);
  }

  function closeSettingsModal() {
    setOpenMenu(null);
    setIsSettingsOpen(false);
  }

  return (
    <div className={`wavemaker-root ${className ?? ""}`.trim()}>
      <div className="gameWrapper">
        <div className="gameContainer">
          <div className="gameUiRail gameUiRailTop">
            <div className="gameUiRailContent gameUiRailContentTop">
              <div className={dropdownClassNames.level} ref={levelDropdownRef}>
                <button
                  type="button"
                  id="dropdownMenu2"
                  ref={levelButtonRef}
                  className="dropdown-toggle"
                  aria-haspopup="true"
                  aria-expanded={openMenu === "level"}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpenMenu((prev) => (prev === "level" ? null : "level"));
                  }}
                >
                  <span
                    className="star-icon level-select-button-star star-icon-button-unhighlighted"
                    aria-hidden="true"
                  />
                  <span className="level-select-button-label">{levelButtonLabel}</span>
                </button>
                <div
                  className="dropdown-menu dropdown-menu-right dropdown-menu-level-select dropdown-menu-overlay"
                  id="level-select-menu"
                  role="menu"
                  onClick={() => setOpenMenu(null)}
                />
              </div>
            </div>
          </div>
          <div className="gameCanvasViewport">
            <canvas id="myCanvas" ref={canvasRef} aria-label={t("aria.canvas")} />
          </div>
          <div className="gameUiRail gameUiRailBottom">
            <div className="gameUiRailContent gameUiRailContentBottom">
              <div className="overlayButtonGroup">
                <button
                  type="button"
                  className="overlayIconButton sound-toggle-button settings-toggle-button"
                  aria-label={t("aria.openSettings")}
                  aria-haspopup="dialog"
                  aria-expanded={isSettingsOpen}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    openSettingsModal();
                  }}
                >
                  <span className="settings-toggle-icon" aria-hidden="true">
                    <img src={gearIconSrc} alt="" />
                  </span>
                </button>
                <button
                  type="button"
                  className={`overlayIconButton sound-toggle-button ${!isSoundOn ? "is-muted" : ""}`}
                  aria-label={isSoundOn ? t("aria.soundMute") : t("aria.soundUnmute")}
                  aria-pressed={!isSoundOn}
                  onClick={() => {
                    setIsSoundOn((prev) => !prev);
                  }}
                >
                  <span className="sound-toggle-icon sound-toggle-icon--on" aria-hidden="true">
                    <svg viewBox="0 0 64 64" focusable="false">
                      <path d="M8 24h12l16-12v40L20 40H8z" fill="currentColor" />
                      <path
                        d="M41 24c4 4 4 12 0 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeLinecap="round"
                      />
                      <path
                        d="M47 18c7 7 7 21 0 28"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <span className="sound-toggle-icon sound-toggle-icon--off" aria-hidden="true">
                    <svg viewBox="0 0 64 64" focusable="false">
                      <path d="M8 24h12l16-12v40L20 40H8z" fill="currentColor" />
                      <path
                        d="M42 24L56 38"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeLinecap="round"
                      />
                      <path
                        d="M56 24L42 38"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="4"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div
            className={`settingsModalBackdrop ${isSettingsOpen ? "settingsModalBackdrop-open" : ""}`}
            onClick={closeSettingsModal}
          >
            <div
              className="settingsModal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="wavemaker-settings-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="settingsModalHeader">
                <h2 id="wavemaker-settings-title" className="settingsModalTitle">
                  {t("settings.title")}
                </h2>
                <button
                  type="button"
                  className="settingsModalCloseButton"
                  aria-label={t("aria.closeSettings")}
                  onClick={closeSettingsModal}
                >
                  <span className="settingsModalCloseIcon" aria-hidden="true">
                    x
                  </span>
                </button>
              </div>
              <div className="settingsModalBody">
                <button
                  type="button"
                  id="flipHorizontal"
                  className="settingsFlipButton"
                  aria-label={t("aria.flipGameHorizontally")}
                >
                  <span>{t("settings.flipGameHorizontally")}</span>
                  <span className="settingsFlipIcon" aria-hidden="true">
                    <FlipHorizontalIcon />
                  </span>
                </button>
                <button
                  type="button"
                  className="settingsReferenceButton"
                  aria-pressed={showReferenceLines}
                  onClick={() => {
                    setShowReferenceLines((prev) => !prev);
                  }}
                >
                  <span>{t("settings.showReferenceLines")}</span>
                  <span className="settingsReferenceCheckbox" aria-hidden="true">
                    <input
                      type="checkbox"
                      checked={showReferenceLines}
                      readOnly
                      tabIndex={-1}
                      aria-hidden="true"
                    />
                  </span>
                </button>
                <div className={dropdownClassNames.speed} ref={speedDropdownRef}>
                  <button
                    type="button"
                    id="dropdownMenuSpeed"
                    className="dropdown-toggle settingsSpeedButton"
                    aria-haspopup="true"
                    aria-expanded={openMenu === "speed"}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setOpenMenu((prev) => (prev === "speed" ? null : "speed"));
                    }}
                  >
                    {timeSpeedButtonLabel}
                  </button>
                  <div
                    className="dropdown-menu dropdown-menu-speed-overlay settingsDropdownMenu"
                    id="time-speed-select-menu"
                    ref={speedMenuRef}
                    role="menu"
                    onClick={() => setOpenMenu(null)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <input
          type="checkbox"
          id="sound-button"
          checked={isSoundOn}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="soundStateInput"
        />
      </div>
      <audio id="winSound">
        <source src={winSoundSrc} type="audio/mpeg" />
      </audio>
      <audio id="winSound2">
        <source src={winSoundCelebrationSrc} type="audio/mpeg" />
      </audio>
    </div>
  );
}
